import { matchPath } from 'react-router-dom';
import { compile } from 'path-to-regexp';
// NOTE: This file imports urlHelpers.js, which may lead to circular dependency
import { stringify } from './urlHelpers';

/**
 * Query params a canonical /s URL keeps — an allowlist, so that every new tracking or UI param
 * (gclid, fbclid, utm_*, rdt_cid, oppref, mapSearch, sort, dates, …) is dropped without anyone
 * having to remember it here.
 *
 * Kept: the space-type facet (category levels), the booking-model facet (listing type) and the
 * page number from page 2 on (each results page lists different listings and is its own canonical;
 * pointing them all at page 1 hid every listing behind page 1 from the canonical graph).
 *
 * Dropped on purpose: bounds, address, origin and keywords. Map-area and free-text variants are
 * duplicates of the city pages (/coworking/:citySlug owns the geo intent) and are noindexed by the
 * search page; dates, price, seats and sort only re-order or narrow the same inventory.
 */
const isCanonicalSearchParam = key =>
  key === 'pub_listingType' || /^pub_categoryLevel[123]$/.test(key);

const canonicalPageNumber = value => {
  const page = Number.parseInt(value, 10);
  return `${page}` === `${value}`.trim() && page > 1 ? page : null;
};

/**
 * Canonical query string of a search URL.
 *
 * @param {string} search - location.search (with or without leading `?`)
 * @returns {string} query string without leading `?`, or empty string
 */
export const canonicalSearchQueryString = search => {
  const params = new URLSearchParams(typeof search === 'string' ? search : '');
  const picked = {};
  params.forEach((value, key) => {
    if (isCanonicalSearchParam(key) && `${value}`.trim() !== '') {
      picked[key] = value;
    } else if (key === 'page') {
      const page = canonicalPageNumber(value);
      if (page) {
        picked.page = page;
      }
    }
  });
  return stringify(picked);
};

/**
 * The canonical path of a listing (decision D1 of the SEO overhaul): slugless, so a renamed
 * listing keeps its URL. Every machine signal — rel=canonical, og:url, Product @id and
 * offers.url, ItemList entries, sitemaps — uses this; visible links keep the slug.
 *
 * @param {string} listingId listing UUID
 * @returns {string} `/l/<uuid>`
 */
export const listingCanonicalPath = listingId => `/l/${listingId}`;

const LISTING_ROUTE_NAMES = ['ListingPage', 'ListingPageCanonical'];
const SEARCH_ROUTE_NAMES = ['SearchPage', 'SearchPageWithListingType'];

const withoutTrailingSlash = pathname => {
  const trimmed = `${pathname || ''}`.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
};

const findRouteByName = (nameToFind, routes) => routes.find(route => route.name === nameToFind);

/**
 * E.g. ```const toListingPath = toPathByRouteName('ListingPage', routes);```
 * Then we can generate listing paths with given params (```toListingPath({ id: uuidX })```)
 */
const toPathByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Path "${nameToFind}" was not found.`);
  }
  return compile(route.path);
};

/**
 * Shorthand for single path call. (```pathByRouteName('ListingPage', routes, { id: uuidX });```)
 */
export const pathByRouteName = (nameToFind, routes, params = {}) => {
  const hasEmptySlug = params && params.hasOwnProperty('slug') && params.slug === '';
  const pathParams = hasEmptySlug ? { ...params, slug: 'no-slug' } : params;
  return toPathByRouteName(nameToFind, routes)(pathParams);
};

/**
 * Find the matching routes and their params for the given pathname
 *
 * @param {String} pathname - Full URL path from root with possible
 * search params and hash included
 *
 * @return {Array<{ route, params }>} - All matches as { route, params } objects if matches has
 * exact flag set to false. If not, an array containing just the first matched exact route is returned.
 */
export const matchPathname = (pathname, routeConfiguration) => {
  const matchedRoutes = routeConfiguration.reduce((matches, route) => {
    const { path, exact = true } = route;
    const match = matchPath(pathname, { path, exact });
    if (match) {
      matches.push({
        route,
        params: match.params || {},
      });
    }
    return matches;
  }, []);

  const matchedExactRoute = matchedRoutes.find(r => {
    return r.exact === true || r.exact == null;
  });

  // We return matched 'exact' path route only if such exists
  // and all matches if no exact flag exists.
  return matchedExactRoute ? [matchedExactRoute] : matchedRoutes;
};

/**
 * ResourceLocatorString is used to direct webapp to correct page.
 * In contrast to Universal Resource Locator (URL), this doesn't contain protocol, host, or port.
 */
export const createResourceLocatorString = (
  routeName,
  routes,
  pathParams = {},
  searchParams = {},
  hash = ''
) => {
  const searchQuery = stringify(searchParams);
  const includeSearchQuery = searchQuery.length > 0 ? `?${searchQuery}` : '';
  const path = pathByRouteName(routeName, routes, pathParams);
  return `${path}${includeSearchQuery}${hash}`;
};

/**
 * Find component related to route name
 * E.g. `const PageComponent = findComponentByRouteName('CheckoutPage', routes);`
 * Then we can call static methods of given component:
 * `dispatch(PageComponent.setInitialValues({ listing, bookingDates }));`
 *
 * @param {String} nameToFind - Route name
 * @param {Array<{ route }>} routes - Route configuration as flat array.
 *
 * @return {Route} - Route that matches the given route name.
 */
export const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Component "${nameToFind}" was not found.`);
  }
  return route;
};

/**
 * Get the canonical path for the given location — an allowlist, not a blocklist:
 *
 * - never a hash, never a trailing slash (except the root '/'),
 * - listings ('ListingPage' and 'ListingPageCanonical') → `/l/<uuid>` (listingCanonicalPath),
 * - search routes keep only the canonical search params (canonicalSearchQueryString),
 * - every other route drops the query entirely. No other public route renders different
 *   indexable content per query param, and ad click ids (gclid, fbclid, utm_*, rdt_cid, oppref)
 *   used to make every landing URL its own canonical. Routes that used to opt in with
 *   `canonicalIgnoresQuery: true` (the city pages and their type pages, whose `?typ=` only
 *   preselects a pill) are covered by this default; the flag is kept as documentation.
 *
 * @param {Array<{ route }>} routes - Route configuration as flat array
 * @param {Object} location - location object from React Router
 * @param {boolean} [pathOnly] - drop the search query even on search routes (referrer policies)
 *
 * @return {String} Canonical path of the given location
 */
export const canonicalRoutePath = (routes, location, pathOnly = false) => {
  const { pathname: rawPathname, search } = location || {};
  const pathname = withoutTrailingSlash(rawPathname);

  const matches = matchPathname(pathname, routes);
  const match = matches.length === 1 ? matches[0] : null;
  const routeName = match?.route?.name;

  if (LISTING_ROUTE_NAMES.includes(routeName) && match.params?.id) {
    return listingCanonicalPath(match.params.id);
  }

  if (SEARCH_ROUTE_NAMES.includes(routeName) && !pathOnly) {
    const canonicalSearch = canonicalSearchQueryString(search);
    return canonicalSearch ? `${pathname}?${canonicalSearch}` : pathname;
  }

  return pathname;
};

// Regex that replaces {userId}, {listingId} or {userEmail} in the href string
// with URI encoded user email and ID
export const replaceParamsInHref = (href, params) => {
  return href.replace(/{(userId|userEmail|listingId)}/g, (match, key) => {
    if (params[key] != null) {
      return encodeURIComponent(params[key]);
    }
    return match;
  });
};

// This function generates data required to render ExternalLink and InternalLink components.
// It is given a URL and it replaces the {userId} and {userEmail} placeholders
// with the corresponding user data, encodes the values, and determines whether the URL
// is internal or external. If it's an internal link, the function resolves the appropriate
// route and returns an object containing route information. For external links, it returns
// the processed link without route details.
export const generateLinkProps = (type, href, routeConfiguration, userId, userEmail, listingId) => {
  const params = { userId, userEmail, listingId };
  const processedLink = replaceParamsInHref(href, params);

  const isInternalLink = type === 'internal' || href.charAt(0) === '/';

  if (isInternalLink) {
    const testURL = new URL('http://my.marketplace.com' + processedLink);

    const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
    if (matchedRoutes.length > 0) {
      const found = matchedRoutes[0];
      const to = { search: testURL.search, hash: testURL.hash };

      // Return an object with route info
      return {
        link: processedLink,
        route: {
          name: found.route?.name,
          params: found.params,
          to,
        },
      };
    }
  }
  // If not internal, return the processed external link without route info
  return {
    link: processedLink,
  };
};
